import { useMemo, useCallback, useEffect, useRef, useState } from 'react'
import { useGallery } from '../context/GalleryContext'
import { useLightbox } from '../hooks/useLightbox'
import { useGridZoom } from '../hooks/useGridZoom'
import { useGridVirtualization } from '../hooks/useGridVirtualization'
import { gridGalleryConfig } from '../config/gridGalleryConfig'
import GridImage from './GridImage'
import GridZoomControls from './GridZoomControls'
import AlbumSelector from './AlbumSelector'
import { Lightbox } from './lightbox'

export default function PhotosGrid() {
  const {
    albums,
    images,
    selectedAlbumId,
    setSelectedAlbumId,
    isLoading,
    currentPage,
    totalPages,
    totalImageCount,
    goToPage,
    likedImageIds,
    showLikedOnly,
    setShowLikedOnly,
    purchasedImageIds,
    showPurchasedOnly,
    setShowPurchasedOnly,
  } = useGallery()

  // Measure album selector height for zoom contentOffset
  const albumSelectorRef = useRef<HTMLDivElement>(null)
  const [, setAlbumSelectorHeight] = useState(0)

  useEffect(() => {
    const el = albumSelectorRef.current
    if (!el) return

    const measure = () => {
      setAlbumSelectorHeight(el.getBoundingClientRect().height)
    }

    measure()

    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(el)
    return () => resizeObserver.disconnect()
  }, [albums])

  // Filter images based on liked or purchased filter
  const displayImages = useMemo(() => {
    if (showLikedOnly) return images
    if (showPurchasedOnly) return images.filter(img => purchasedImageIds.has(img.id))
    return images
  }, [images, showLikedOnly, showPurchasedOnly, purchasedImageIds])

  // Grid zoom (column count management + gestures)
  const {
    zoomLevelIndex,
    maxZoomIndex,
    columns,
    gap,
    zoomIn,
    zoomOut,
    zoomWrapperRef,
    scrollContainerRef,
  } = useGridZoom({
    imageCount: displayImages.length,
    contentOffset: 0,
  })

  // Virtualization (windowed rendering)
  const {
    visibleRange,
    totalHeight,
    offsetTop,
    visibleCount,
    rowHeight,
    onScroll,
  } = useGridVirtualization({
    imageCount: displayImages.length,
    columns,
    gap,
    containerRef: scrollContainerRef,
  })

  // Lightbox state
  const lightbox = useLightbox({ images: displayImages, loop: true })

  const handleImageClick = useCallback((index: number) => {
    lightbox.open(index)
  }, [lightbox])

  // Scroll to viewed image when lightbox closes (not on zoom)
  const prevLightboxOpen = useRef(lightbox.state.isOpen)
  useEffect(() => {
    const wasOpen = prevLightboxOpen.current
    prevLightboxOpen.current = lightbox.state.isOpen

    if (wasOpen && !lightbox.state.isOpen && lightbox.lastViewedIndex >= 0) {
      const container = scrollContainerRef.current
      if (!container) return

      const row = Math.floor(lightbox.lastViewedIndex / columns)
      const targetScroll = row * rowHeight - container.clientHeight / 3

      setTimeout(() => {
        container.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth',
        })
      }, 100)
    }
  }, [lightbox.state.isOpen, lightbox.lastViewedIndex, columns, rowHeight, scrollContainerRef])

  // Preload thumbnails ahead of visible range
  useEffect(() => {
    const start = visibleRange.endIndex
    const end = Math.min(start + gridGalleryConfig.preload.thumbnailsAhead, displayImages.length)

    for (let i = start; i < end; i++) {
      const url = displayImages[i]?.urls?.thumbnail
      if (url) {
        const img = new Image()
        img.src = url
      }
    }
  }, [visibleRange.endIndex, displayImages])

  // Reset scroll when album/filter/page changes
  useEffect(() => {
    const container = scrollContainerRef.current
    if (container) container.scrollTop = 0
  }, [selectedAlbumId, showLikedOnly, showPurchasedOnly, currentPage, scrollContainerRef])

  // Visible slice of images
  const visibleImages = useMemo(() => {
    const slice: { image: typeof displayImages[0]; globalIndex: number }[] = []
    for (let i = visibleRange.startIndex; i < visibleRange.endIndex; i++) {
      if (displayImages[i]) {
        slice.push({ image: displayImages[i], globalIndex: i })
      }
    }
    return slice
  }, [displayImages, visibleRange.startIndex, visibleRange.endIndex])

  // Page navigation
  const showPagination = totalPages > 1
  const canGoPrev = currentPage > 0
  const canGoNext = currentPage < totalPages - 1
  const pageStart = currentPage * 1000 + 1
  const pageEnd = Math.min((currentPage + 1) * 1000, totalImageCount)

  if (isLoading && displayImages.length === 0) {
    return (
      <div className="w-full h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-400 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading gallery...</p>
        </div>
      </div>
    )
  }

  if (displayImages.length === 0) {
    return (
      <div className="w-full h-screen bg-gray-900 flex flex-col">
        {albums.length > 0 && (
          <AlbumSelector
            albums={albums}
            selectedAlbumId={selectedAlbumId}
            onSelect={setSelectedAlbumId}
            totalImageCount={totalImageCount}
            likedCount={likedImageIds.size}
            showLikedOnly={showLikedOnly}
            onToggleLiked={setShowLikedOnly}
            purchasedCount={purchasedImageIds.size}
            showPurchasedOnly={showPurchasedOnly}
            onTogglePurchased={setShowPurchasedOnly}
          />
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-4">
            <svg className="w-16 h-16 text-gray-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="text-white text-xl font-semibold mb-2">
              {showLikedOnly ? 'No liked photos yet' : showPurchasedOnly ? 'No unlocked photos yet' : selectedAlbumId ? 'No photos in this album' : 'No photos yet'}
            </h2>
            <p className="text-gray-400">
              {showLikedOnly ? 'Heart some photos to see them here!' : showPurchasedOnly ? 'Unlock photos to see them here!' : 'Check back soon for new photos!'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div ref={zoomWrapperRef} className="w-full h-screen overflow-hidden bg-gray-900 flex flex-col relative">
      {/* Album Selector — fixed header */}
      <div ref={albumSelectorRef} className="flex-shrink-0">
        <AlbumSelector
          albums={albums}
          selectedAlbumId={selectedAlbumId}
          onSelect={setSelectedAlbumId}
          totalImageCount={totalImageCount}
          likedCount={likedImageIds.size}
          showLikedOnly={showLikedOnly}
          onToggleLiked={setShowLikedOnly}
          purchasedCount={purchasedImageIds.size}
          showPurchasedOnly={showPurchasedOnly}
          onTogglePurchased={setShowPurchasedOnly}
        />
      </div>

      {/* Scrollable grid container — fills remaining space below header */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        style={{
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
        }}
        onScroll={onScroll}
      >
        {/* Total height spacer for scroll bar accuracy */}
        <div style={{ height: totalHeight, position: 'relative' }}>
          {/* Offset spacer to position visible items */}
          <div
            style={{
              position: 'absolute',
              top: offsetTop,
              left: 0,
              right: 0,
            }}
          >
            {/* CSS Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${columns}, 1fr)`,
                gap: `${gap}px`,
              }}
            >
              {visibleImages.map(({ image, globalIndex }) => (
                <GridImage
                  key={image.id}
                  image={image}
                  visibleCount={visibleCount}
                  columns={columns}
                  onClick={() => handleImageClick(globalIndex)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loading overlay for page transitions */}
      {isLoading && displayImages.length > 0 && (
        <div className="absolute inset-0 z-40 bg-gray-900/60 flex items-center justify-center pointer-events-none">
          <div className="w-10 h-10 border-4 border-purple-400 border-t-purple-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Page navigation arrows — only shown when multiple pages exist */}
      {showPagination && (
        <>
          {/* Previous page arrow — left side */}
          {canGoPrev && (
            <button
              onClick={() => goToPage(currentPage - 1)}
              className="absolute left-1 sm:left-3 top-1/2 -translate-y-1/2 z-50 w-10 h-16 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all active:scale-95"
              aria-label="Previous page"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next page arrow — right side */}
          {canGoNext && (
            <button
              onClick={() => goToPage(currentPage + 1)}
              className="absolute right-1 sm:right-20 top-1/2 -translate-y-1/2 z-50 w-10 h-16 bg-black/50 hover:bg-black/70 backdrop-blur-sm rounded-lg flex items-center justify-center transition-all active:scale-95"
              aria-label="Next page"
            >
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </>
      )}

      {/* Grid Zoom Controls */}
      <GridZoomControls
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        zoomLevelIndex={zoomLevelIndex}
        maxZoomIndex={maxZoomIndex}
      />

      {/* Instructions tooltip */}
      <div className="absolute bottom-6 left-6 z-40 pointer-events-none animate-fade-out">
        <div className="bg-black/60 backdrop-blur-sm text-white text-xs rounded-lg px-3 py-2">
          <span className="hidden sm:inline">Ctrl + scroll to zoom grid | Scroll to browse</span>
          <span className="sm:hidden">Pinch to zoom grid | Scroll to browse</span>
        </div>
      </div>

      {/* Page indicator + photo count — bottom center */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-none">
        <div className="bg-black/40 text-white/70 text-xs rounded-full px-3 py-1">
          {showPagination
            ? `${pageStart}-${pageEnd} of ${totalImageCount} photos`
            : `${displayImages.length} photo${displayImages.length !== 1 ? 's' : ''}`
          }{showLikedOnly ? ' liked' : ''}
        </div>
      </div>

      {/* Lightbox viewer */}
      <Lightbox
        images={displayImages}
        state={lightbox.state}
        onClose={lightbox.close}
      />

      <style>{`
        @keyframes fadeOut {
          0% { opacity: 1; }
          70% { opacity: 1; }
          100% { opacity: 0; }
        }
        .animate-fade-out {
          animation: fadeOut 4s ease-out forwards;
        }
      `}</style>
    </div>
  )
}
